import React, { useCallback, useEffect, useRef, useState } from 'react';
import { updatePinChat } from 'api/chat/pin/update-pin.api';
import { useSubscription } from 'hooks/use-subscription.hook';
import { useChatContext } from 'pages/chat/context';
import { useNavigate, useParams } from 'react-router-dom';
import { IContact, IConversation } from 'types/chat';
import { formatDateRelative } from 'utils/date/formatDateRelative';
import { useOnClickOutside } from 'hooks/use-on-click-outside.hook';
import clsx from 'clsx';
import { updateSeenMessage } from 'api/chat/message/update-seen-message.api';

interface Props {
  item: IConversation;
  isPinChat?: boolean;
}

const avatarDefault =
  'https://png.pngtree.com/png-vector/20191101/ourmid/pngtree-cartoon-color-simple-male-avatar-png-image_1934459.jpg';

const onLoadImgError = (srcImg) => {
  srcImg.current.src = avatarDefault;
};

const renderGroupAvatar = (item, ownerAvatarRef, avatarRef) => {
  const userGroupOwner = item.members?.find((member) => member._id === item.owner_id);
  const otherUser = item.members?.find((member) => member._id !== item.owner_id);

  const avatarGroupOwner = userGroupOwner?.path_avatar || avatarDefault;
  const avatarOtherUser = otherUser?.path_avatar || avatarDefault;

  return (
    <div className='item-group-avatar'>
      <div className='item-avatar owner'>
        <img
          src={avatarGroupOwner}
          ref={ownerAvatarRef}
          alt='user icon'
          onError={() => onLoadImgError(ownerAvatarRef)}
        />
      </div>
      <div className='item-avatar other'>
        <img src={avatarOtherUser} ref={avatarRef} alt='user icon' onError={() => onLoadImgError(avatarRef)} />
      </div>
      <div className='item-status online'></div>
    </div>
  );
};

const renderAvatar = (otherContact, avatarRef) => {
  const avatarOtherUser = otherContact?.path_avatar || avatarDefault;

  return (
    <div className='item-single-avatar'>
      <img src={avatarOtherUser} ref={avatarRef} alt='user icon' onError={() => onLoadImgError(avatarRef)} />
      <div className='item-status online'></div>
    </div>
  );
};

const ChatItem = (props: Props) => {
  const { item, isPinChat = false } = props;
  const [otherContact, setOtherContact] = useState<IContact | null>(null);
  const [, setGroupContact] = useState<IContact[]>([]);
  const [isShowModal, toggleShowModal] = useState<boolean>(false);
  const [unpinModal, setUnpinShowModal] = useState<boolean>(false);
  const ownerAvatarRef = useRef(null);
  const avatarRef = useRef(null);
  const pinRef = useRef(null);
  const unpinRef = useRef(null);
  const subscription = useSubscription();
  const chatContext = useChatContext();
  const navigate = useNavigate();
  const params = useParams<'conversationId'>();
  const seenMsgRef = useRef<string>('');
  const { selectedConversation, setSelectedConversation } = chatContext;

  const onPairing = () => {
    setSelectedConversation(item._id);
    item._id && navigate(`/chat/${item._id}`);
  };

  useOnClickOutside(pinRef, () => toggleShowModal(false));
  useOnClickOutside(unpinRef, () => setUnpinShowModal(false));

  useEffect(() => {
    if (item.is_group_chat) {
      setGroupContact(item.members);
    } else {
      const otherUser = item?.members?.find((member) => member?._id !== chatContext.userObject._id);

      setOtherContact(otherUser);
    }
  }, [chatContext, chatContext.userObject._id, item]);

  const { is_group_chat } = item;

  const handlePinChat = () => {
    toggleShowModal(false);
    const data = {
      conversation_id: item._id,
      user_id: chatContext.userObject._id,
      pin_chat: true,
    };
    const pinData = updatePinChat(data).subscribe({
      next: () => {
        chatContext.getConversations();
      },
    });

    subscription.add(pinData);
  };

  const handleUnPinChat = () => {
    setUnpinShowModal(false);
    const data = {
      conversation_id: item._id,
      user_id: chatContext.userObject._id,
      pin_chat: false,
    };

    const pinData = updatePinChat(data).subscribe({
      next: () => {
        chatContext.getConversations();
      },
    });

    subscription.add(pinData);
  };

  const getUnreadItem = useCallback(() => {
    if (
      (item._id === params.conversationId && item.latest_message?._id) ||
      seenMsgRef.current === item.latest_message?._id
    ) {
      if (
        !item.latest_message.read_by.includes(chatContext.userObject._id) &&
        seenMsgRef.current !== item.latest_message._id
      ) {
        // Call API seen msg
        const updateSeenMessageSub = updateSeenMessage(item.latest_message._id).subscribe();

        subscription.add(updateSeenMessageSub);

        seenMsgRef.current = item.latest_message._id;
      }

      return false;
    }

    return item.latest_message?.read_by && !item.latest_message.read_by.includes(chatContext.userObject._id);
  }, [
    chatContext.userObject?._id,
    item?._id,
    item?.latest_message?._id,
    item?.latest_message?.read_by,
    params?.conversationId,
    subscription,
  ]);

  return (
    <div
      className={clsx('chat-item-wrapper', {
        '-is-unread': getUnreadItem(),
        active: selectedConversation === item._id,
      })}
      onClick={() => onPairing()}
    >
      <>{is_group_chat ? renderGroupAvatar(item, ownerAvatarRef, avatarRef) : renderAvatar(otherContact, avatarRef)}</>
      <div className='item-content'>
        <div className='item-username'>
          <p>{item?.chat_name || otherContact?.emp_name}</p>
        </div>
        <div className='item-message'>
          <div
            className='content'
            dangerouslySetInnerHTML={{ __html: sanitize(item?.latest_message?.content || '') }}
          />
        </div>
      </div>

      <div className='item-time'>
        {item?.latest_message?.content && (
          <>
            <span className='timestamp'>{formatDateRelative(item?.latest_message?.updated_at || '')}</span>
          </>
        )}
      </div>

      <div className='item-date'>
        <p>{item?.updatedAt}</p>
        {isPinChat && (
          <div className='item-pinned' ref={unpinRef}>
            <img src='https://img.icons8.com/material-outlined/24/000000/pin.png' />
            <span className='item-three-dot h-d_none' onClick={() => setUnpinShowModal(!unpinModal)}>
              <i className='fas fa-ellipsis-h'></i>
            </span>
            {unpinModal && (
              <div className='item-pinned-modal shadow' onClick={handleUnPinChat}>
                <div>
                  <img src='https://img.icons8.com/material-outlined/24/000000/unpin.png' />
                </div>
                <div>
                  <p className='item-pinned-title'>Unpin chat</p>
                  <p className='item-pinned-subtitle'>Unpin this chat</p>
                </div>
              </div>
            )}
          </div>
        )}

        {
          <div className='item-pinned' ref={pinRef}>
            {item.numOfUnreadMessage && (
              <div className='item-unread-message'>
                <span>{item.numOfUnreadMessage < 100 ? item.numOfUnreadMessage : '99+'}</span>
              </div>
            )}
            {!isPinChat && (
              <span className='item-three-dot item-dot-unpin h-d_none ' onClick={() => toggleShowModal(true)}>
                <i className='fas fa-ellipsis-h'></i>
              </span>
            )}
            {isShowModal && !isPinChat && (
              <div className='item-pinned-modal shadow' onClick={handlePinChat}>
                <div>
                  <img src='https://img.icons8.com/material-outlined/24/000000/pin.png' />
                </div>
                <div>
                  <p className='item-pinned-title'>Pin chat</p>
                  <p className='item-pinned-subtitle'>Pin this chat</p>
                </div>
              </div>
            )}
          </div>
        }
      </div>
    </div>
  );
};

export default ChatItem;
